import { Clock, MapPin, User, BookOpen, ExternalLink } from 'lucide-react';

// German → English type translation
const TYPE_LABELS = {
  'Vorlesung': 'Lecture',
  'Vorlesung (fakultativ)': 'Lecture (optional)',
  'Vorlesung (Teleteaching)': 'Lecture (online)',
  'Seminar': 'Seminar',
  'Seminar (fakultativ)': 'Seminar (optional)',
  'Praktikum': 'Lab',
  'Praktikum (fakultativ)': 'Lab (optional)',
  'Praktische Übung': 'Practical Exercise',
  'Praktische Übung (fakultativ)': 'Practical Exercise (optional)',
  'Konsultation': 'Consultation',
  'Tutorium': 'Tutorial',
  'Übung': 'Exercise',
  'Übung (fakultativ)': 'Exercise (optional)',
  'Übung (Teleteaching)': 'Exercise (online)',
  'Klausur': 'Exam',
  'Klausur im Vorlesungszeitraum': 'Mid-term Exam',
  'Mündliche Prüfung': 'Oral Exam',
  'Kolloquium': 'Colloquium',
  'Operativer Termin': 'Operational Event',
  'Reservierung': 'Reservation',
  'Sonstige Lehre': 'Other Teaching',
  'alternative Prüfungsleistung': 'Alternative Assessment',
  'alternative Prüfungsleistung im Semester': 'In-semester Assessment',
};

function translateType(germanType) {
  if (!germanType) return 'Event';
  return TYPE_LABELS[germanType] || germanType;
}

const TYPE_COLORS = {
  'Lecture': 'bg-blue-500/15 border-blue-500/30 dark:bg-blue-500/10 dark:border-blue-500/20',
  'Seminar': 'bg-violet-500/15 border-violet-500/30 dark:bg-violet-500/10 dark:border-violet-500/20',
  'Lab': 'bg-rose-500/15 border-rose-500/30 dark:bg-rose-500/10 dark:border-rose-500/20',
  'Consultation': 'bg-teal-500/15 border-teal-500/30 dark:bg-teal-500/10 dark:border-teal-500/20',
  'Tutorial': 'bg-amber-500/15 border-amber-500/30 dark:bg-amber-500/10 dark:border-amber-500/20',
  'Exercise': 'bg-orange-500/15 border-orange-500/30 dark:bg-orange-500/10 dark:border-orange-500/20',
  'Exam': 'bg-red-600/15 border-red-600/30 dark:bg-red-600/10 dark:border-red-600/20',
  'Custom': 'bg-yellow-400/15 border-yellow-400/40 dark:bg-yellow-400/10 dark:border-yellow-400/20',
};

const TYPE_DOT_COLORS = {
  'Lecture': 'bg-blue-500',
  'Seminar': 'bg-violet-500',
  'Lab': 'bg-rose-500',
  'Consultation': 'bg-teal-500',
  'Tutorial': 'bg-amber-500',
  'Exercise': 'bg-orange-500',
  'Exam': 'bg-red-600',
  'Custom': 'bg-yellow-400',
};

function getTypeStyle(englishLabel) {
  const key = Object.keys(TYPE_COLORS).find(k => englishLabel.toLowerCase().startsWith(k.toLowerCase()));
  return {
    color: TYPE_COLORS[key] || 'bg-muted border-border',
    dot: TYPE_DOT_COLORS[key] || 'bg-muted-foreground',
  };
}

function formatTime(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
}

function buildMapsUrl(room, locationPlan) {
  const parts = [room, locationPlan, 'TU Ilmenau'].filter(Boolean);
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts.join(', '))}`;
}

export default function EventCard({ event, isEnrolled, onToggleEnroll, isCustom, showEnrolledDot }) {
  const englishType = isCustom ? 'Custom' : translateType(event.type);
  const { color: colorClass, dot: dotColor } = getTypeStyle(englishType);

  const startTime = isCustom ? event.start_time : formatTime(event.start);
  const endTime = isCustom ? event.end_time : formatTime(event.end);
  const locationText = [event.room, event.location_plan].filter(Boolean).join(' | ');

  return (
    <div
      className={`border rounded-sm p-3 transition-colors duration-150 ${colorClass} ${isEnrolled ? 'ring-1 ring-emerald-500/50' : ''}`}
      data-testid={`event-card-${(event.name || event.title || '').replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColor}`} />
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
              {englishType}
            </span>
          </div>
          <h4 className="text-sm font-semibold leading-tight truncate font-['Outfit']">
            {isCustom ? event.title : event.name}
          </h4>
        </div>
        {!isCustom && isEnrolled && showEnrolledDot && (
          <div className="flex items-center flex-shrink-0">
            <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" title="Enrolled" />
          </div>
        )}
      </div>

      <div className="mt-2 space-y-1">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock className="w-3 h-3 flex-shrink-0" />
          <span>{startTime} - {endTime}</span>
        </div>
        {locationText && (
          <a
            href={buildMapsUrl(event.room, event.location_plan)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs text-primary hover:underline underline-offset-2 group"
            data-testid={`location-link-${(event.name || event.title || '').replace(/\s+/g, '-').toLowerCase()}`}
            onClick={(e) => e.stopPropagation()}
          >
            <MapPin className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{locationText}</span>
            <ExternalLink className="w-2.5 h-2.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
          </a>
        )}
        {event.professor && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <User className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{event.professor}</span>
          </div>
        )}
        {event.module && event.module !== event.name && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <BookOpen className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{event.module}</span>
          </div>
        )}
        {isCustom && event.description && (
          <p className="text-xs text-muted-foreground mt-1">{event.description}</p>
        )}
      </div>
    </div>
  );
}
